import React, { useRef, useState, useEffect, useCallback } from "react";
import { Modal } from "@/components/ui/modal";
import { Scissors, Volume2, VolumeX, Type, Plus, Trash2, Check, Loader2, Image as ImageIcon } from "lucide-react";
import { mediaUrl } from "@/lib/api";
import { cn } from "@/lib/utils";

const CAP = 60; // max clip length (seconds)

function fmtT(s) {
  s = Math.max(0, s || 0);
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  const d = Math.floor((s % 1) * 10);
  return `${m}:${String(sec).padStart(2, "0")}.${d}`;
}

export function VideoEditor({ open, src, initial, onClose, onApply }) {
  const videoRef = useRef(null);
  const stageRef = useRef(null);
  const [dur, setDur] = useState(0);
  const [cur, setCur] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [sub, setSub] = useState("trim"); // trim | audio | text

  const [trimStart, setTrimStart] = useState(initial?.trim_start ?? 0);
  const [trimEnd, setTrimEnd] = useState(initial?.trim_end ?? 0);
  const [volume, setVolume] = useState(initial?.volume ?? 1);
  const [mute, setMute] = useState(initial?.mute ?? false);
  const [texts, setTexts] = useState(initial?.texts ?? []);
  const [sel, setSel] = useState(null);
  const [coverTime, setCoverTime] = useState(initial?.cover_time ?? (initial?.trim_start ?? 0));
  const drag = useRef(null);

  const onMeta = (e) => {
    const d = e.target.duration;
    if (!isFinite(d)) return;
    setDur(d);
    const end = Math.min(initial?.trim_end || d, trimStart + CAP, d);
    setTrimEnd(end || Math.min(d, CAP));
  };

  // Enforce trim window during playback (loop within [trimStart, trimEnd]).
  const onTime = (e) => {
    const v = e.target;
    setCur(v.currentTime);
    if (trimEnd && v.currentTime >= trimEnd) {
      v.currentTime = trimStart;
      if (!v.paused) v.play().catch(() => {});
    }
  };

  const play = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      if (v.currentTime < trimStart || v.currentTime >= trimEnd) v.currentTime = trimStart;
      v.play().catch(() => {});
    } else v.pause();
  };

  useEffect(() => {
    const v = videoRef.current;
    if (v) { v.muted = mute; v.volume = mute ? 0 : volume; }
  }, [mute, volume, open]);

  const length = Math.max(0, (trimEnd || dur) - trimStart);

  const addText = () => {
    const t = { id: Math.random().toString(36).slice(2), text: "Your text", x: 0.5, y: 0.5, size: 0.07, start: trimStart, end: trimEnd || dur };
    setTexts((s) => [...s, t]);
    setSel(t.id);
    setSub("text");
  };
  const upd = (id, patch) => setTexts((s) => s.map((t) => (t.id === id ? { ...t, ...patch } : t)));
  const del = (id) => { setTexts((s) => s.filter((t) => t.id !== id)); if (sel === id) setSel(null); };

  const startDrag = useCallback((e, id) => {
    e.preventDefault();
    setSel(id);
    drag.current = id;
  }, []);
  const onStageMove = (e) => {
    if (!drag.current || !stageRef.current) return;
    const r = stageRef.current.getBoundingClientRect();
    const pt = e.touches ? e.touches[0] : e;
    const nx = Math.min(1, Math.max(0, (pt.clientX - r.left) / r.width));
    const ny = Math.min(1, Math.max(0, (pt.clientY - r.top) / r.height));
    upd(drag.current, { x: nx, y: ny });
  };
  const endDrag = () => { drag.current = null; };

  const apply = () => {
    const v = videoRef.current;
    if (v) v.pause();
    onApply({
      trim_start: Number(trimStart.toFixed(2)),
      trim_end: Number((trimEnd || dur).toFixed(2)),
      volume: mute ? 0 : Number(volume.toFixed(2)),
      mute,
      cover_time: Number(Math.min(Math.max(coverTime, trimStart), trimEnd || dur).toFixed(2)),
      texts: texts.map((t) => ({ text: t.text, x: t.x, y: t.y, size: t.size, start: t.start, end: t.end })),
      _duration: Number(length.toFixed(2)),
    });
  };

  const stageH = stageRef.current?.clientHeight || 300;
  const tooLong = length > CAP + 0.05;

  return (
    <Modal open={open} onClose={onClose} title="Edit video" testid="video-editor">
      <div className="p-4">
        {/* Preview stage */}
        <div
          ref={stageRef}
          data-testid="editor-stage"
          className="relative mx-auto aspect-[9/16] max-h-[46vh] w-auto overflow-hidden rounded-2xl border border-am-border bg-black"
          onMouseMove={onStageMove}
          onMouseUp={endDrag}
          onMouseLeave={endDrag}
          onTouchMove={onStageMove}
          onTouchEnd={endDrag}
        >
          <video
            ref={videoRef}
            src={mediaUrl(src)}
            playsInline
            onClick={play}
            onLoadedMetadata={onMeta}
            onTimeUpdate={onTime}
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            className="h-full w-full object-contain"
          />
          {texts.map((t) => (
            <div
              key={t.id}
              data-testid={`editor-text-${t.id}`}
              onMouseDown={(e) => startDrag(e, t.id)}
              onTouchStart={(e) => startDrag(e, t.id)}
              style={{
                left: `${t.x * 100}%`, top: `${t.y * 100}%`,
                transform: `translate(${-t.x * 100}%, ${-t.y * 100}%)`,
                fontSize: `${Math.max(12, t.size * stageH)}px`,
              }}
              className={cn(
                "absolute cursor-move select-none whitespace-pre rounded-md bg-black/45 px-2 py-0.5 font-bold leading-tight text-white",
                sel === t.id && "ring-2 ring-aura-cyan"
              )}
            >
              {t.text || " "}
            </div>
          ))}
          {!playing && (
            <button onClick={play} data-testid="editor-play" className="absolute inset-0 grid place-items-center">
              <span className="grid h-14 w-14 place-items-center rounded-full bg-black/50 text-white">▶</span>
            </button>
          )}
        </div>
        <div className="mt-1.5 text-center text-xs text-am-text3" data-testid="editor-length">
          Clip length: <span className={cn("font-semibold", tooLong ? "text-aura-magenta" : "text-am-text")}>{fmtT(length)}</span> / {CAP}s max
        </div>

        {/* Sub-tabs */}
        <div className="mt-3 flex gap-1.5">
          {[{ k: "trim", i: Scissors, l: "Trim" }, { k: "audio", i: mute ? VolumeX : Volume2, l: "Audio" }, { k: "text", i: Type, l: "Text" }, { k: "cover", i: ImageIcon, l: "Cover" }].map((o) => (
            <button key={o.k} onClick={() => setSub(o.k)} data-testid={`editor-tab-${o.k}`}
              className={cn("flex flex-1 items-center justify-center gap-1.5 rounded-xl border py-2 text-xs font-semibold",
                sub === o.k ? "border-transparent aura-gradient text-black" : "border-am-border bg-am-surface2 text-am-text2")}>
              <o.i className="h-4 w-4" /> {o.l}
            </button>
          ))}
        </div>

        <div className="mt-3 min-h-[120px]">
          {sub === "trim" && (
            <div className="space-y-3">
              <div>
                <div className="flex justify-between text-xs text-am-text2"><span>Start</span><span className="font-mono text-white">{fmtT(trimStart)}</span></div>
                <input type="range" min={0} max={dur || 0} step={0.1} value={trimStart} data-testid="editor-trim-start"
                  onChange={(e) => { const v = Math.min(parseFloat(e.target.value), (trimEnd || dur) - 0.5); setTrimStart(v); if (videoRef.current) videoRef.current.currentTime = v; }}
                  className="w-full accent-aura-magenta" />
              </div>
              <div>
                <div className="flex justify-between text-xs text-am-text2"><span>End</span><span className="font-mono text-white">{fmtT(trimEnd)}</span></div>
                <input type="range" min={0} max={dur || 0} step={0.1} value={trimEnd} data-testid="editor-trim-end"
                  onChange={(e) => { const v = Math.max(parseFloat(e.target.value), trimStart + 0.5); setTrimEnd(Math.min(v, trimStart + CAP)); if (videoRef.current) videoRef.current.currentTime = v - 0.05; }}
                  className="w-full accent-aura-cyan" />
              </div>
              {tooLong && <p className="text-xs text-aura-magenta">Trim to {CAP}s or less — the end was capped automatically.</p>}
            </div>
          )}

          {sub === "audio" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold text-white">Mute original audio</div>
                <button onClick={() => setMute((m) => !m)} data-testid="editor-mute"
                  className={cn("relative h-6 w-11 rounded-full transition-colors", mute ? "aura-gradient" : "bg-am-surface3")}>
                  <span className={cn("absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all", mute ? "left-[22px]" : "left-0.5")} />
                </button>
              </div>
              <div className={cn(mute && "pointer-events-none opacity-40")}>
                <div className="flex justify-between text-xs text-am-text2"><span>Volume</span><span className="font-mono text-white">{Math.round(volume * 100)}%</span></div>
                <input type="range" min={0} max={2} step={0.05} value={volume} data-testid="editor-volume"
                  onChange={(e) => setVolume(parseFloat(e.target.value))} className="w-full accent-aura-gold" />
              </div>
            </div>
          )}

          {sub === "text" && (
            <div className="space-y-3">
              <button onClick={addText} data-testid="editor-add-text" className="flex w-full items-center justify-center gap-1.5 rounded-xl border border-am-border bg-am-surface2 py-2 text-xs font-semibold text-am-text2 hover:text-white">
                <Plus className="h-4 w-4" /> Add text
              </button>
              {texts.length === 0 && <p className="text-center text-xs text-am-text3">Add text, then drag it on the video to position it.</p>}
              {texts.map((t) => (
                <div key={t.id} className={cn("rounded-xl border p-3", sel === t.id ? "border-aura-cyan/50 bg-am-surface2" : "border-am-border bg-am-surface2/50")} onClick={() => setSel(t.id)}>
                  <div className="flex items-center gap-2">
                    <input value={t.text} onChange={(e) => upd(t.id, { text: e.target.value })} data-testid={`editor-text-input-${t.id}`} maxLength={60}
                      className="flex-1 rounded-lg border border-am-border bg-am-bg px-2.5 py-1.5 text-sm text-white outline-none focus:border-am-border-strong" />
                    <button onClick={() => del(t.id)} data-testid={`editor-text-del-${t.id}`} className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-am-text2 hover:text-aura-magenta"><Trash2 className="h-4 w-4" /></button>
                  </div>
                  <div className="mt-2">
                    <div className="flex justify-between text-[11px] text-am-text3"><span>Size</span></div>
                    <input type="range" min={0.03} max={0.16} step={0.005} value={t.size} onChange={(e) => upd(t.id, { size: parseFloat(e.target.value) })} className="w-full accent-aura-magenta" />
                  </div>
                  <div className="mt-1 grid grid-cols-2 gap-2">
                    <div>
                      <div className="flex justify-between text-[11px] text-am-text3"><span>Shows</span><span className="font-mono">{fmtT(t.start)}</span></div>
                      <input type="range" min={trimStart} max={trimEnd || dur} step={0.1} value={t.start} onChange={(e) => upd(t.id, { start: Math.min(parseFloat(e.target.value), t.end - 0.2) })} className="w-full accent-aura-cyan" />
                    </div>
                    <div>
                      <div className="flex justify-between text-[11px] text-am-text3"><span>Hides</span><span className="font-mono">{fmtT(t.end)}</span></div>
                      <input type="range" min={trimStart} max={trimEnd || dur} step={0.1} value={t.end} onChange={(e) => upd(t.id, { end: Math.max(parseFloat(e.target.value), t.start + 0.2) })} className="w-full accent-aura-cyan" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {sub === "cover" && (
            <div className="space-y-3" data-testid="editor-cover">
              <p className="text-xs text-am-text3">Scrub to the frame you want as the video's cover, then set it.</p>
              <div>
                <div className="flex justify-between text-xs text-am-text2"><span>Preview frame</span><span className="font-mono text-white">{fmtT(cur)}</span></div>
                <input type="range" min={trimStart} max={trimEnd || dur} step={0.1} value={Math.min(Math.max(cur, trimStart), trimEnd || dur)} data-testid="editor-cover-scrub"
                  onChange={(e) => { const v = parseFloat(e.target.value); const vid = videoRef.current; if (vid) { vid.pause(); vid.currentTime = v; } setCur(v); }}
                  className="w-full accent-aura-gold" />
              </div>
              <button onClick={() => { const vid = videoRef.current; const t = vid ? vid.currentTime : cur; setCoverTime(t); }} data-testid="editor-set-cover"
                className="flex w-full items-center justify-center gap-1.5 rounded-xl border border-am-border bg-am-surface2 py-2 text-xs font-semibold text-am-text2 hover:text-white">
                <ImageIcon className="h-4 w-4" /> Set current frame as cover
              </button>
              <div className="flex items-center justify-center gap-1.5 text-xs text-aura-mint" data-testid="editor-cover-at">
                <Check className="h-3.5 w-3.5" /> Cover set at {fmtT(Math.min(Math.max(coverTime, trimStart), trimEnd || dur))}
              </div>
            </div>
          )}
        </div>

        <button onClick={apply} data-testid="editor-apply" className="mt-4 flex w-full items-center justify-center gap-2 rounded-full aura-gradient py-3 text-sm font-bold text-black">
          <Check className="h-4 w-4" /> Done editing
        </button>
      </div>
    </Modal>
  );
}
